import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { theme } from 'config/theme';
import { ClassAttributes, HTMLAttributes, useRef, useState } from 'react';
import { ExtraProps } from 'react-markdown';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import CheckFillIcon from 'remixicon-react/CheckFillIcon';
import FileCopyLineIcon from 'remixicon-react/FileCopyLineIcon';

function HighlightSyntax(
  props: ClassAttributes<HTMLElement> & HTMLAttributes<HTMLElement> & ExtraProps,
) {
  const { children, className, node, ...rest } = props;
  const match = /language-(\w+)/.exec(className || '');
  const blockRef = useRef<HTMLDivElement | null>(null);
  const [openTooltip, setOpenTooltip] = useState(false);

  const copyBlock = async () => {
    try {
      if (blockRef?.current?.innerText) {
        setOpenTooltip(true);
        const htmlContent = blockRef.current.innerHTML;
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([htmlContent], { type: 'text/html' }),
            'text/plain': new Blob([blockRef.current.innerText], { type: 'text/plain' }),
          }),
        ]);

        setTimeout(() => {
          setOpenTooltip(false);
        }, 1200);
      }
    } catch (error) {
      console.warn('COPY_BLOCK_ERROR', error);
    }
  };

  return match ? (
    <>
      <Box ref={blockRef}>
        <SyntaxHighlighter
          {...(rest as any)}
          language={match[1]}
          style={vs2015}
          wrapLongLines
          PreTag="div"
          children={String(children).replace(/\n$/, '')}
        />
      </Box>
      <Box
        width="100%"
        p={1}
        sx={{
          backgroundColor: vs2015['hljs']?.background || theme.palette.custom.navy,
          mt: '0.6px',
        }}
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
      >
        <Tooltip open={openTooltip} placement="top" title={<Typography>Copied</Typography>}>
          <IconButton onClick={copyBlock}>
            {openTooltip ? (
              <CheckFillIcon size={20} color={theme.palette.custom.white} />
            ) : (
              <FileCopyLineIcon size={20} color={theme.palette.custom.white} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </>
  ) : (
    <code {...rest} className={className}>
      {children}
    </code>
  );
}

export default HighlightSyntax;
